<template>
  <manual-page
    title="Control de Inventario"
    description="Guía completa para gestionar el stock de ingredientes, registrar movimientos y mantener el control de tu inventario."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'inventoriesIndex' }"
    action-text="Ir a Inventario"
  >
    <!-- Overview -->
    <h2>Vista General</h2>
    <p>
      El módulo de inventario te permite controlar el stock de ingredientes y materiales
      de tu sucursal. Un buen control de inventario es esencial para evitar
      interrupciones en la producción.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Con el módulo de inventario puedes:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Ver el stock actual de cada producto</li>
        <li>Registrar entradas de mercancía</li>
        <li>Registrar salidas y consumos</li>
        <li>Configurar alertas de stock bajo</li>
        <li>Consultar el historial de movimientos</li>
        <li>Planificar compras según la demanda</li>
      </ul>
    </div>

    <!-- Inventory List -->
    <h2>Lista de Inventario</h2>
    <p>
      La pantalla principal muestra todos los productos registrados con su información de stock.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Información Mostrada</h3>
          <p class="text-gray-600 mb-2">Cada producto muestra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Nombre:</strong> Identificador del producto</li>
            <li><strong>Cantidad actual:</strong> Stock disponible</li>
            <li><strong>Unidad:</strong> Kilogramos, unidades, litros, etc.</li>
            <li><strong>Stock mínimo:</strong> Nivel de alerta configurado</li>
            <li><strong>Estado:</strong> Normal, bajo o agotado</li>
            <li><strong>Última actualización:</strong> Fecha del último movimiento</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Filtros y Búsqueda</h3>
          <p class="text-gray-600">
            Usa la barra de búsqueda para encontrar productos específicos.
            También puedes filtrar por categoría o estado del stock.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Los productos con stock bajo se destacan visualmente.
        Revisa esta lista regularmente para planificar tus compras.
      </p>
    </div>

    <!-- Stock Alerts -->
    <h2>Alertas de Stock Bajo</h2>
    <p>
      El sistema te alerta cuando un producto está por agotarse para que puedas
      reabastecerte a tiempo.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Cómo Funcionan las Alertas</h3>
          <p class="text-gray-600">
            Cuando la cantidad de un producto cae por debajo del stock mínimo
            configurado, el sistema lo marca como "bajo" y puede enviarte
            una notificación.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Configurar Stock Mínimo</h3>
          <p class="text-gray-600">
            Desde el detalle de cada producto puedes definir el nivel de stock
            mínimo. Considera tu consumo semanal y tiempo de reabastecimiento.
          </p>
        </div>
      </div>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> No ignores las alertas de stock bajo. Quedarte
        sin ingredientes durante la producción causa retrasos en las entregas
        y afecta la satisfacción del cliente.
      </p>
    </div>

    <!-- Recording Entries -->
    <h2>Registrar Entrada de Mercancía</h2>
    <p>
      Cuando recibes productos de tus proveedores, debes registrar la entrada
      para actualizar el stock.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Acceder al Producto</h3>
          <p class="text-gray-600">
            Busca el producto en la lista y haz clic para ver su detalle.
            Luego selecciona "Registrar entrada" o el botón de agregar.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Ingresar Datos</h3>
          <p class="text-gray-600 mb-2">Completa la información de la entrada:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Cantidad:</strong> Cuánto recibiste</li>
            <li><strong>Fecha:</strong> Cuándo llegó la mercancía</li>
            <li><strong>Proveedor:</strong> De quién compraste (opcional)</li>
            <li><strong>Costo:</strong> Precio pagado (opcional)</li>
            <li><strong>Notas:</strong> Observaciones adicionales</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Confirmar Entrada</h3>
          <p class="text-gray-600">
            Verifica que los datos sean correctos y guarda. El stock se
            actualiza automáticamente sumando la cantidad ingresada.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Registra las entradas inmediatamente al recibir la mercancía.
        Esto mantiene el inventario preciso y evita confusiones.
      </p>
    </div>

    <!-- Recording Exits -->
    <h2>Registrar Salida de Inventario</h2>
    <p>
      Las salidas de inventario se registran cuando se consume producto en producción
      o por otros motivos como merma o devolución.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Tipos de Salida</h3>
          <p class="text-gray-600 mb-2">Puedes registrar salidas por diferentes motivos:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Producción:</strong> Uso de ingredientes para elaborar alimento</li>
            <li><strong>Merma:</strong> Producto que se echó a perder o caducó</li>
            <li><strong>Devolución:</strong> Regreso de producto al proveedor</li>
            <li><strong>Ajuste:</strong> Corrección por diferencias de inventario</li>
            <li><strong>Otro:</strong> Cualquier otro motivo</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Registrar la Salida</h3>
          <p class="text-gray-600 mb-2">Para registrar una salida:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Busca el producto y abre su detalle</li>
            <li>Selecciona "Registrar salida"</li>
            <li>Ingresa la cantidad utilizada</li>
            <li>Selecciona el motivo de la salida</li>
            <li>Agrega notas si es necesario</li>
            <li>Confirma para actualizar el stock</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="info-box">
      <p class="text-sm text-gray-700">
        <strong>Nota:</strong> En algunos casos, las salidas por producción pueden
        registrarse automáticamente según la configuración del sistema.
      </p>
    </div>

    <!-- Movement History -->
    <h2>Historial de Movimientos</h2>
    <p>
      El sistema guarda un registro completo de todos los movimientos de inventario.
      Este historial es útil para auditorías y análisis.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Ver Historial por Producto</h3>
          <p class="text-gray-600">
            Desde el detalle de cada producto puedes ver todos sus movimientos:
            entradas, salidas, fechas, cantidades y quién los registró.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Información del Movimiento</h3>
          <p class="text-gray-600 mb-2">Cada movimiento muestra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Fecha y hora:</strong> Cuándo se registró</li>
            <li><strong>Tipo:</strong> Entrada o salida</li>
            <li><strong>Cantidad:</strong> Cuánto se movió</li>
            <li><strong>Motivo:</strong> Por qué se hizo el movimiento</li>
            <li><strong>Usuario:</strong> Quién lo registró</li>
            <li><strong>Stock resultante:</strong> Cantidad después del movimiento</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Filtrar Historial</h3>
          <p class="text-gray-600">
            Puedes filtrar el historial por rango de fechas, tipo de movimiento
            o usuario para encontrar información específica.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Revisa el historial si notas discrepancias en el stock.
        Puedes identificar errores o movimientos no autorizados.
      </p>
    </div>

    <!-- Physical Inventory -->
    <h2>Conteo Físico de Inventario</h2>
    <p>
      Periódicamente debes hacer un conteo físico para verificar que el stock
      en el sistema coincida con el stock real.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Programar Conteos</h3>
          <p class="text-gray-600">
            Se recomienda hacer conteos físicos al menos una vez al mes
            para los productos de mayor movimiento, y trimestral para otros.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Realizar el Conteo</h3>
          <p class="text-gray-600">
            Cuenta físicamente cada producto y compara con el stock registrado
            en el sistema. Anota las diferencias encontradas.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Ajustar Diferencias</h3>
          <p class="text-gray-600">
            Si hay diferencias, registra movimientos de ajuste para que
            el stock del sistema coincida con el conteo real.
            Documenta el motivo de cada ajuste.
          </p>
        </div>
      </div>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> Investiga las diferencias significativas.
        Pueden indicar problemas como robos, errores de registro o mermas
        no documentadas.
      </p>
    </div>

    <!-- Planning Purchases -->
    <h2>Planificación de Compras</h2>
    <p>
      Usa la información de inventario para planificar tus compras y evitar
      quedarte sin ingredientes.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Revisar Stock Actual</h3>
          <p class="text-gray-600">
            Consulta el inventario y el Programa de Producción para
            estimar cuánto consumirás en los próximos días.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Identificar Necesidades</h3>
          <p class="text-gray-600">
            Compara tu stock actual con la demanda esperada.
            Identifica qué productos necesitas comprar pronto.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Considerar Tiempos de Entrega</h3>
          <p class="text-gray-600">
            Toma en cuenta cuánto tardan tus proveedores en entregar.
            Ordena con suficiente anticipación para no quedarte sin stock.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Configura el stock mínimo considerando el tiempo de
        reabastecimiento. Por ejemplo, si tu proveedor tarda 3 días y consumes
        5 kg diarios, el mínimo debería ser al menos 15 kg.
      </p>
    </div>

    <!-- Best Practices -->
    <h2>Buenas Prácticas</h2>
    <ul>
      <li><strong>Registra movimientos en tiempo real:</strong> No dejes para después</li>
      <li><strong>Sé preciso:</strong> Usa básculas y medidas exactas</li>
      <li><strong>Documenta todo:</strong> Incluye notas y motivos en cada movimiento</li>
      <li><strong>Haz conteos regulares:</strong> Verifica el stock físico periódicamente</li>
      <li><strong>Configura alertas:</strong> Define stocks mínimos realistas</li>
      <li><strong>Revisa el historial:</strong> Analiza tendencias de consumo</li>
      <li><strong>Planifica compras:</strong> Anticípate a las necesidades</li>
      <li><strong>Controla la merma:</strong> Registra y analiza el desperdicio</li>
    </ul>

    <!-- Support -->
    <h2>¿Necesitas ayuda?</h2>
    <p>Si tienes problemas con el inventario:</p>
    <ul>
      <li><strong>WhatsApp:</strong> +52 811 669 1329</li>
      <li><strong>Correo:</strong> soporte@trunimal.com</li>
    </ul>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualInventory",
  components: { ManualPage },
  data() {
    return {
      relatedLinks: [
        { route: "manualProductionSchedule", label: "Programa de Producción" },
        { route: "manualOrders", label: "Órdenes" },
        { route: "manualAccounting", label: "Contabilidad" },
        { route: "manualGettingStarted", label: "Primeros Pasos" },
      ],
    };
  },
};
</script>
